import React from "react";
import { Link } from "react-router";
import { Table, Tr, Td, Sort } from "reactable";
import { colors } from "material-ui/styles";

import { makeFilmDetailString, makeEventDateRangeLabel } from "../../util";
import EventBucketStore from "../../stores/EventBucketStore";
import UserStore from "../../stores/UserStore";

const SUCCESS_COLOR = "#4CAF50";

export const Status = ({ film, virtual_content, detailed, inWidget }) => {
  if (film && !virtual_content) {
    virtual_content = film.virtual_content;
  }

  if (!virtual_content) {
    if (detailed && !inWidget) {
      return <span>&ndash;</span>;
    } else {
      return (
        <span style={{ fontWeight: "bold", color: colors.red600 }}>
          {inWidget ? `No virtual content` : `Needs upload`}
        </span>
      );
    }
  }
  let {
    type,
    status,
    label,
    platform,
    caption_tracks,
    status_percent,
    status_description
  } = virtual_content;
  if (type === "vod") {
    if (platform === "cinesend" || platform === "eventive") {
      status = "legacy_unsupported";
    }
    const messages = {
      legacy_unsupported: {
        message: (
          <span>
            No longer supported – re-upload required{" "}
            <a
              href="https://help.eventive.org/en/articles/5292118-legacy-asset-pathway-deprecation-information-may-2021"
              target="_blank"
            >
              (?)
            </a>
          </span>
        ),
        color: colors.red600
      },
      pending_upload: {
        message: `Upload in progress`,
        color: colors.orange600
      },
      pending_transcoding: {
        message: "Transcoding in progress",
        color: colors.orange600
      },
      transcoding_failure: {
        message: `Transcoding failed`,
        color: colors.orange600
      },
      vod_upload_started: {
        message: `VOD upload in progress`,
        color: colors.orange600
      },
      vod_upload_failure: {
        message: `VOD upload failed`,
        color: colors.orange600
      },
      vod_upload_complete: {
        message: `VOD waiting for transcoding`,
        color: colors.orange600
      },
      vod_transcode_started: {
        message:
          (platform || "").indexOf("ev_next") === 0 && !status_percent
            ? `VOD transcoding queued`
            : `VOD transcoding in progress${
                status_percent ? ` (${status_percent}%)` : ""
              }`,
        color: colors.orange600
      },
      vod_transcode_failure: {
        message: `VOD transcoding failed${
          status_description ? ` (${status_description})` : ""
        }`,
        color: colors.orange600
      },
      vod_transcode_complete: {
        message: `VOD waiting for publishing`,
        color: colors.orange600
      },
      vod_publish_started: {
        message: `VOD publishing in progress`,
        color: colors.orange600
      },
      vod_publish_failure: {
        message: `VOD publishing failed${
          status_description ? ` (${status_description})` : ""
        }`,
        color: colors.orange600
      },
      ready: {
        message: (
          <span>
            Streaming VOD ready{" "}
            {caption_tracks && caption_tracks.length ? (
              <span title="Closed captions">
                +{" "}
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{
                    width: 24,
                    height: 24,
                    verticalAlign: -3,
                    margin: "-4px 0"
                  }}
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z" />
                </svg>
              </span>
            ) : null}
          </span>
        ),
        color: SUCCESS_COLOR
      }
    };
    const { message, color } = messages[status] || {
      message: status,
      color: colors.orange600
    };
    return (
      <span style={{ fontWeight: "bold", color }}>
        {message}
        {UserStore.isGod() && (
          <span
            style={{
              color: "rgba(0,0,0,0.4)",
              display: "block",
              fontSize: "0.7em",
              marginTop: "0.1em",
              lineHeight: "1.1em"
            }}
          >
            {virtual_content.id} / {virtual_content.platform}
            {virtual_content.additional_regions ? (
              <div style={{ color: "#3194d5", marginTop: "0.1em" }}>
                Replicated to {virtual_content.additional_regions.sort().join(", ")}
              </div>
            ) : null}
          </span>
        )}
      </span>
    );
  } else if (type === "livestream" || type === "broadcast") {
    const messages = {
      livestream_not_created: {
        message:
          type === "broadcast" ? "Real-time broadcast ready" : "Not started",
        color: colors.red600
      },
      livestream_creating: {
        message: "Starting",
        color: SUCCESS_COLOR
      },
      livestream_creating_preview: {
        message: "Starting in preview window",
        color: SUCCESS_COLOR
      },
      livestream_previewing: {
        message: "Started in preview window",
        color: SUCCESS_COLOR
      },
      livestream_previewing_creating: {
        message: "Going live",
        color: SUCCESS_COLOR
      },
      livestream_create_failure: {
        message: "Failed to start",
        color: colors.red600
      },
      livestream_encode_failure: {
        message: "Live encoding failed",
        color: colors.red600
      },
      livestream_created: {
        message: "Started",
        color: SUCCESS_COLOR
      },
      livestream_stopping: {
        message: "Stopping",
        color: colors.red600
      },
      livestream_stop_failure: {
        message: "Failed to stop",
        color: colors.red600
      },
      livestream_publish_failure: {
        message: `VOD publishing failed${
          status_description ? ` (${status_description})` : ""
        }`,
        color: colors.orange600
      },
      livestream_stopped: {
        message: "Stopped",
        color: colors.red600
      }
    };
    const { message, color } = messages[status];
    return <span style={{ fontWeight: "bold", color }}>{message}</span>;
  } else if (type === "external" && status === "ready") {
    return (
      <span style={{ fontWeight: "bold", color: SUCCESS_COLOR }}>
        External content ready
      </span>
    );
  }
  return null;
};

export const TitleDeliveryStatus = ({ film, title_delivery, extended }) => {
  if (film && !title_delivery) {
    title_delivery = film.title_delivery;
  }

  const messages = {
    requested: {
      message: `Invitation sent to creator`,
      color: colors.grey600
    },
    draft: {
      message: `Delivery draft started`,
      color: colors.orange600
    },
    submitted_awaiting_delivery: {
      message: `Awaiting delivery`,
      color: colors.orange600
    },
    delivery_received: {
      message: `Delivery received, pending QC`,
      color: colors.green600
    },
    action_needed: {
      message: `Needs action from content creator`,
      color: colors.red600
    },
    delivery_approved: {
      message: `Delivery approved, asset ready`,
      color: colors.green600
    },
    delivery_approved_missing_virtual: {
      message: `Print delivery approved, but missing virtual content`,
      color: colors.red600
    }
  };

  let status = title_delivery
    ? title_delivery.status
    : film && film.title_requested_media && film.title_requested_media.length
    ? "requested"
    : "none";

  // In the case when Simple DCP has approved the print delivery, but the virtual content is missing
  // (likely because the user chose to manually upload it, but didn't).
  if (
    status === "delivery_approved" &&
    film &&
    film.title_requested_media &&
    film.title_requested_media.includes("virtual") &&
    !film.virtual_content
  ) {
    status = "delivery_approved_missing_virtual";
  }

  if (status === "none") {
    return <span>–</span>;
  }

  // fixme - don't show requested items if already uploaded for virtual
  // if no title requested media do something special (check heartland)
  const requestedDetail =
    film && film.title_requested_media && film.title_requested_media.length
      ? ` (${film.title_requested_media.sort().join(", ")})`
      : "";

  const { message, color } = messages[status];
  return (
    <span style={{ fontWeight: "bold", color }}>
      {message}
      {requestedDetail}
      {extended && title_delivery ? (
        <div
          style={{
            fontWeight: "normal",
            color: "rgba(0, 0, 0, 0.4)",
            fontSize: "9pt"
          }}
        >
          <span style={{ fontFamily: "monospace, sans-serif" }}>
            {title_delivery.delivery_identifier}
          </span>
          {title_delivery.status_description ? (
            <span> &middot; {title_delivery.status_description}</span>
          ) : null}
        </div>
      ) : null}
    </span>
  );
};

class FilmTable extends React.Component {
  constructor(props) {
    super(props);
    this._makeRowForEvent = this._makeRowForEvent.bind(this);
  }

  getStyles() {
    return {};
  }

  _makeRowForEvent(film, detailed) {
    return (
      <Tr key={film.id}>
        <Td column="Name">{film.name}</Td>
        <Td column="Date" value={film.start_time}>
          {makeEventDateRangeLabel({
            is_dated: true,
            start_time: film.start_time,
            end_time: film.end_time,
            timezone: (
              EventBucketStore.getEventBucketById(film.event_bucket) || {}
            ).timezone
          })}
        </Td>
        <Td column="Event">{film.event_bucket_name}</Td>
        <Td column="Status">
          <Status film={film} detailed={detailed} />
        </Td>
        <Td column="Delivery Status">
          <TitleDeliveryStatus
            film={film}
            detailed={detailed}
            extended={true}
          />
        </Td>
        <Td column="Details">{makeFilmDetailString(film.details)}</Td>
        <Td column="Organization">{film.owner_organization}</Td>
        <Td column="Actions">
          {this.props.livestreamView ? (
            <div>
              <Link
                to={{
                  pathname: `/event_buckets/${film.event_bucket}/virtual_festival/live/${film.id}`,
                  state: { fromListing: true }
                }}
              >
                Manage livestream
              </Link>
            </div>
          ) : this.props.creatorView ? (
            <div>
              <Link
                to={{
                  pathname: `/creator_hub/films/${film.id}`,
                  state: { fromListing: true }
                }}
              >
                Manage content
              </Link>
            </div>
          ) : UserStore.hasPermission("preview_virtual_screening") &&
            !UserStore.hasPermission("manage_films") ? (
            <div>
              <Link
                to={{
                  pathname: `/event_buckets/${film.event_bucket}/films/${film.id}`,
                  state: { fromListing: true }
                }}
              >
                View details
              </Link>
            </div>
          ) : (
            <div>
              <Link
                to={{
                  pathname: `/event_buckets/${film.event_bucket}/films/${film.id}/edit`,
                  state: { fromListing: true }
                }}
              >
                Edit film
              </Link>
            </div>
          )}
        </Td>
      </Tr>
    );
  }

  render() {
    let styles = this.getStyles(),
      { films, creatorView, livestreamView } = this.props,
      eventBucket =
        (films.length &&
          EventBucketStore.getEventBucketById(films[0].event_bucket)) ||
        {},
      isVirtual = eventBucket.enable_virtual_festival,
      isTitleDelivery = eventBucket.enable_title_delivery,
      detailed = (UserStore.getUser() || {}).type === "festival_organizer";

    const caseInsensitiveSort = function(a, b) {
      return a.localeCompare(b, undefined, {sensitivity: 'base'}) 
    };

    const tableProps = {
      className: "bordered striped responsive-table",
      sortable: livestreamView
        ? [{ column: "Name", sortFunction: caseInsensitiveSort }, "Date", "Status"]
        : creatorView
        ? [{ column: "Name", sortFunction: caseInsensitiveSort }, "Organization", "Event"]
        : [{ column: "Name", sortFunction: caseInsensitiveSort }, "Details"],
      defaultSort: livestreamView
        ? { column: "Date", direction: "asc" }
        : { column: "Name", direction: "asc" },
      columns: livestreamView
        ? ["Name", "Date", "Status", "Actions"]
        : creatorView
        ? ["Name", "Organization", "Event", "Status", "Actions"]
        : isVirtual
        ? ["Name", "Details"]
            .concat(isTitleDelivery ? ["Delivery Status"] : [])
            .concat(["Status", "Actions"])
        : ["Name", "Details", "Actions"],
      filterable: creatorView
        ? ["Name", "Organization", "Event"]
        : ["Name", "Details"]
    };

    // filter "accident" unnamed films out
    films = films.filter(f => !(f.created_at === f.updated_at && !f.name));

    return (
      <div>
        <style>{`
          .reactable-filterer input {
            width: 200px;
          }
        `}</style>
        <div>
          <Table
            {...tableProps}
            filterPlaceholder={
              this.props.creatorView
                ? `Type to filter screenings...`
                : this.props.livestreamView
                ? "Type to filter livestreams..."
                : "Type to filter films..."
            }
          >
            {films.map(film => this._makeRowForEvent(film, detailed))}
          </Table>
        </div>
        {films.length === 0 ? (
          livestreamView ? (
            <div className="empty-table-message">
              No upcoming livestreams.{" "}
              <Link
                to={`/event_buckets/${this.props.eventBucket}/events/new?virtual=true`}
              >
                Create a new virtual screening.
              </Link>
            </div>
          ) : creatorView ? (
            <div className="empty-table-message">No screening invites yet.</div>
          ) : (
            <div className="empty-table-message">
              No films yet.{" "}
              <Link to={`/event_buckets/${this.props.eventBucket}/films/new`}>
                Create a new film.
              </Link>
            </div>
          )
        ) : null}
        {this.props.createButton ? (
          <div style={{ marginTop: "1em" }}>{this.props.createButton}</div>
        ) : null}
      </div>
    );
  }
}

export default FilmTable;



// WEBPACK FOOTER //
// ./src/app/components/partials/film-table.jsx